import { useState, useRef, useEffect, memo } from "react";
import { cn } from "@/lib/utils";

interface OptimizedImageProps {
  src: string | null | undefined;
  alt: string;
  width: number;
  height: number;
  className?: string;
  priority?: boolean;
  sizes?: string;
  onLoad?: () => void;
}

/**
 * Optimized image component with:
 * - Proper width/height for CLS prevention
 * - Lazy loading for below-fold images
 * - Priority loading with fetchpriority for LCP
 * - Responsive srcset with mobile versions
 * - Smooth fade-in transition
 */
const OptimizedImage = memo(({
  src,
  alt,
  width,
  height,
  className,
  priority = false,
  sizes = "(max-width: 640px) 45vw, (max-width: 768px) 30vw, 280px",
  onLoad,
}: OptimizedImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  const fallbackSrc = `https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=${width}&h=${height}&fit=crop&q=80&auto=format`;
  
  const imageSrc = src || fallbackSrc;
  
  // Generate mobile version URL for Supabase images
  const getMobileUrl = (url: string): string | null => {
    if (url.includes('supabase.co/storage/') && url.includes('-optimized.jpg')) {
      return url.replace('-optimized.jpg', '-mobile.jpg');
    }
    return null;
  };
  
  // Generate responsive srcset for different screen sizes
  const getSrcSet = () => {
    if (!imageSrc) return undefined;
    
    // For Supabase images, use mobile version if available
    if (imageSrc.includes('supabase.co/storage/')) {
      const mobileUrl = getMobileUrl(imageSrc);
      if (mobileUrl) {
        return `${mobileUrl} 400w, ${imageSrc} 800w`;
      }
      return undefined;
    }
    
    // For Unsplash images, create responsive srcset
    if (imageSrc.includes('unsplash.com')) {
      try {
        const baseUrl = new URL(imageSrc);
        const widths = [400, 600, 800];
        
        return widths
          .filter(w => w <= width * 2)
          .map(w => {
            const url = new URL(baseUrl.toString());
            url.searchParams.set('w', w.toString());
            url.searchParams.set('q', '75');
            url.searchParams.set('auto', 'format');
            url.searchParams.set('fit', 'crop');
            return `${url.toString()} ${w}w`;
          })
          .join(', ');
      } catch {
        return undefined;
      }
    }
    
    return undefined;
  };

  // Check if image is already cached
  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current?.naturalHeight > 0) {
      setIsLoaded(true);
    }
  }, []);

  const handleLoad = () => {
    setIsLoaded(true);
    onLoad?.();
  };

  const handleError = () => {
    setHasError(true);
    setIsLoaded(true);
  };

  return (
    <div 
      className="relative overflow-hidden bg-card"
      style={{ aspectRatio: `${width}/${height}` }}
    >
      {/* Placeholder skeleton - static to reduce CPU */}
      {!isLoaded && (
        <div 
          className="absolute inset-0 bg-muted"
          aria-hidden="true"
        />
      )}
      
      <img
        ref={imgRef}
        src={hasError ? fallbackSrc : imageSrc}
        srcSet={!hasError ? getSrcSet() : undefined}
        sizes={sizes}
        alt={alt}
        width={width}
        height={height}
        loading={priority ? "eager" : "lazy"}
        decoding={priority ? "sync" : "async"}
        // @ts-ignore - fetchpriority is valid but not in React types yet
        fetchpriority={priority ? "high" : "low"}
        onLoad={handleLoad}
        onError={handleError}
        className={cn(
          "h-full w-full object-cover transition-opacity duration-200",
          isLoaded ? "opacity-100" : "opacity-0",
          className
        )}
      />
    </div>
  );
});

OptimizedImage.displayName = 'OptimizedImage';

export default OptimizedImage;
